import { useEffect, useRef, useState } from "react";
import { useSession } from "../session/useSession";
import { ui, disabledStyle } from "../components/uiStyles";

/**
 * Paso 2: Especificaciones del producto confirmado.
 *
 * Backend devuelve TODO en toolOutput (forced tool_choice — sin markdown).
 * Esquema:
 *   summary: string
 *   essentials:  [{name, why_matters}]
 *   recommended: [{name, why_matters}]
 *   optional:    [{name, why_matters}]
 *   price_range: {low, mid, high, currency, note}
 *   common_mistakes: string[]
 *   top_brands: string[]
 *
 * Flow:
 *   1. Al montar → auto-dispara runSpecs("") (usa cache si existe para este producto)
 *   2. Muestra secciones estructuradas: esenciales / recomendables / opcionales / precios / errores / marcas
 *   3. Debajo: input de refinamiento (regenera con contexto adicional, bypasa cache)
 *   4. Botón "Regenerar" fuerza nueva consulta al backend
 */
export function Step2Specs() {
  const { formData, responses, loading, error, runSpecs, goToStep } = useSession();

  const r2 = responses[2];
  const tool = r2?.toolOutput;
  const hasResult = !!tool;

  // Auto-fire al montar si no hay respuesta aún
  const triggered = useRef(false);
  useEffect(() => {
    if (!hasResult && formData.product && !loading && !triggered.current) {
      triggered.current = true;
      runSpecs("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.product]);

  const [refineContext, setRefineContext] = useState("");

  const handleRefine = () => {
    if (!refineContext.trim()) return;
    triggered.current = false;
    runSpecs(refineContext);
    setRefineContext("");
  };

  const handleRegenerate = () => {
    triggered.current = false;
    // force: true bypasea el cache y consulta al backend de nuevo
    runSpecs("", { force: true });
  };

  return (
    <div style={ui.card}>
      <h2 style={ui.h2}>
        Especificaciones de{" "}
        <em style={{ color: "#6c63ff" }}>{formData.product}</em>
      </h2>

      {loading && !hasResult && (
        <p style={{ fontSize: "13px", color: "#888" }}>
          Cargando especificaciones...
        </p>
      )}

      {error && <div style={ui.errorBox}>❌ {error}</div>}

      {hasResult && (
        <>
          {/* Intro en prosa: explica las características relevantes primero */}
          {tool.summary && (
            <p
              style={{
                fontSize: "14px",
                color: "#444",
                lineHeight: 1.6,
                marginBottom: "16px",
                whiteSpace: "pre-line",
              }}
            >
              {tool.summary}
            </p>
          )}

          {/* Precios */}
          {tool.price_range && (
            <section style={sectionBox}>
              <h3 style={sectionTitle}>💰 Rango de precios</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
                <PriceCard
                  label="Básico"
                  value={tool.price_range.low}
                  currency={tool.price_range.currency}
                />
                <PriceCard
                  label="Gama media"
                  value={tool.price_range.mid}
                  currency={tool.price_range.currency}
                />
                <PriceCard
                  label="Premium"
                  value={tool.price_range.high}
                  currency={tool.price_range.currency}
                />
              </div>
              {tool.price_range.note && (
                <p style={{ fontSize: "13px", color: "#666", marginTop: "10px" }}>
                  {tool.price_range.note}
                </p>
              )}
            </section>
          )}

          {/* Errores comunes */}
          {Array.isArray(tool.common_mistakes) && tool.common_mistakes.length > 0 && (
            <section style={sectionBox}>
              <h3 style={sectionTitle}>⚠️ Errores comunes</h3>
              <ul style={{ fontSize: "13px", color: "#555", paddingLeft: "20px", margin: 0 }}>
                {tool.common_mistakes.map((m, i) => (
                  <li key={i} style={{ marginBottom: "4px" }}>{m}</li>
                ))}
              </ul>
            </section>
          )}

          {/* Marcas destacadas */}
          {Array.isArray(tool.top_brands) && tool.top_brands.length > 0 && (
            <section style={sectionBox}>
              <h3 style={sectionTitle}>🏷️ Marcas destacadas</h3>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                {tool.top_brands.map((b, i) => (
                  <span key={i} style={brandChip}>{b}</span>
                ))}
              </div>
            </section>
          )}

          {/* Recap de specs por importancia — al final, como resumen */}
          <SpecSection
            title="✅ Esenciales"
            items={tool.essentials}
            emptyText="Sin datos"
          />
          <SpecSection title="⭐ Recomendables" items={tool.recommended} />
          <SpecSection title="💫 Opcionales" items={tool.optional} />

          {/* Refinamiento */}
          <div
            style={{
              marginTop: "20px",
              padding: "14px",
              background: "#faf9ff",
              borderRadius: "8px",
              border: "1px dashed #d8d4ff",
            }}
          >
            <label style={ui.label}>
              ¿Aclaraciones o contexto adicional? (regenera con más contexto)
            </label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input
                style={ui.input}
                type="text"
                placeholder="ej. lo usaré para trabajo remoto, prefiero marcas reconocidas..."
                value={refineContext}
                onChange={(e) => setRefineContext(e.target.value)}
                onKeyDown={(e) =>
                  e.key === "Enter" && refineContext.trim() && handleRefine()
                }
              />
              <button
                style={{
                  ...ui.primaryBtn,
                  ...(!refineContext.trim() || loading ? disabledStyle : {}),
                }}
                disabled={!refineContext.trim() || loading}
                onClick={handleRefine}
              >
                Refinar
              </button>
            </div>
          </div>

          <div style={{ display: "flex", gap: "10px", marginTop: "16px", flexWrap: "wrap" }}>
            <button style={ui.primaryBtn} onClick={() => goToStep(3)}>
              Buscar opciones de compra →
            </button>
            <button
              style={ui.secondaryBtn}
              disabled={loading}
              onClick={handleRegenerate}
              title="Ignora el resultado guardado y consulta al modelo de nuevo"
            >
              🔄 Regenerar specs
            </button>
            <button style={ui.secondaryBtn} onClick={() => goToStep(1)}>
              ← Volver
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ------- Subcomponentes locales -------

function SpecSection({ title, items, emptyText }) {
  if (!Array.isArray(items) || items.length === 0) {
    if (!emptyText) return null;
    return (
      <section style={sectionBox}>
        <h3 style={sectionTitle}>{title}</h3>
        <p style={{ fontSize: "13px", color: "#888" }}>{emptyText}</p>
      </section>
    );
  }
  return (
    <section style={sectionBox}>
      <h3 style={sectionTitle}>{title}</h3>
      <ul style={{ paddingLeft: 0, margin: 0, listStyle: "none" }}>
        {items.map((it, i) => (
          <li key={i} style={{ marginBottom: "8px" }}>
            <div style={{ fontSize: "14px", fontWeight: 600, color: "#1a1a2e" }}>
              {it.name}
            </div>
            <div style={{ fontSize: "13px", color: "#555", marginTop: "2px" }}>
              {it.why_matters}
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

function PriceCard({ label, value, currency }) {
  return (
    <div
      style={{
        flex: 1,
        minWidth: "120px",
        padding: "10px 12px",
        background: "#ffffff",
        border: "1px solid #e6e4f0",
        borderRadius: "6px",
      }}
    >
      <div style={{ fontSize: "11px", color: "#888", textTransform: "uppercase" }}>
        {label}
      </div>
      <div style={{ fontSize: "14px", fontWeight: 600, color: "#1a4a7a", marginTop: "2px" }}>
        {value} {currency}
      </div>
    </div>
  );
}

// ------- Estilos compartidos -------

const sectionBox = {
  marginTop: "16px",
  padding: "12px 14px",
  background: "#faf9ff",
  borderRadius: "8px",
  border: "1px solid #ece9f7",
};

const sectionTitle = {
  fontSize: "14px",
  fontWeight: 600,
  color: "#1a1a2e",
  margin: "0 0 10px 0",
};

const brandChip = {
  display: "inline-block",
  padding: "4px 10px",
  background: "#ede9fe",
  color: "#4c1d95",
  borderRadius: "12px",
  fontSize: "12px",
  fontWeight: 500,
};
